import { PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Icon, Logo, MAIN_CONTENT_ID, SkipLink, ThemeToggle, cn } from "@sagot/ui";
import { useEffect, type ReactNode } from "react";
import { AuthAside } from "./auth-aside";

export interface AuthHeaderPrompt {
  /** Muted text before the link (hidden < sm), e.g. "New to Sagot?". */
  text: string;
  label: string;
  href: string;
}

export interface AuthHeaderBack {
  label: string;
  href: string;
}

interface AuthLayoutProps {
  /** Document title (`<title>`). */
  title: string;
  /** Right side: prompt + ghost link (login, register, invite). */
  prompt?: AuthHeaderPrompt;
  /** Left side on lg+: back link (verify, 2FA, forgot, reset). */
  back?: AuthHeaderBack;
  width?: "sm" | "md";
  /** `start` keeps tall forms (register, invite) top-aligned on small screens. */
  align?: "center" | "start";
  homeHref?: string;
  children: ReactNode;
}

function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

function HeaderEnd({ prompt }: { prompt?: AuthHeaderPrompt }) {
  const { t } = useTranslation();
  const toggle = (
    <ThemeToggle size="sm" labels={{ trigger: t("theme.toggle"), heading: t("theme.heading"), light: t("theme.light"), dark: t("theme.dark"), system: t("theme.system") }} />
  );
  if (!prompt) return toggle;
  return (
    <div className="flex items-center gap-1">
      <span className="hidden text-sm text-fg-muted sm:inline">{prompt.text}</span>
      <Button asChild variant="ghost" size="sm" className="text-primary-ink">
        <AppLink href={prompt.href}>{prompt.label}</AppLink>
      </Button>
      {toggle}
    </div>
  );
}

/** Split auth layout: navy brand panel (lg+) + form column (mockups/auth/*.html). */
export function AuthLayout({ title, prompt, back, width = "sm", align = "center", homeHref = "/", children }: AuthLayoutProps) {
  const { t } = useTranslation();
  useDocumentTitle(title);
  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[45%_1fr]">
      <SkipLink label={t("shell.skipToContent")} />
      <div className="hidden border-r border-chrome-line bg-chrome lg:block">
        <aside aria-label={PRODUCT_NAME} className="sticky top-0 h-screen overflow-y-auto px-10 py-9 xl:px-14">
          <AuthAside homeHref={homeHref} />
        </aside>
      </div>
      <div className="flex min-h-screen flex-col bg-canvas">
        <header className="flex items-center justify-between gap-3 px-4 py-4 sm:px-8">
          <AppLink href={homeHref} className="lg:hidden" aria-label={t("auth.aside.homeLabel", { product: PRODUCT_NAME })}>
            <Logo />
          </AppLink>
          {back ? (
            <Button asChild variant="ghost" size="sm" className="hidden lg:inline-flex">
              <AppLink href={back.href}>
                <Icon name="arrow-left" />
                {back.label}
              </AppLink>
            </Button>
          ) : (
            <span className="hidden lg:block" />
          )}
          <HeaderEnd prompt={prompt} />
        </header>
        <main
          id={MAIN_CONTENT_ID}
          tabIndex={-1}
          className={cn(
            "flex flex-1 justify-center px-4 pb-12 outline-none sm:px-8",
            align === "start" ? "items-start pt-2 lg:items-center" : "items-center",
          )}
        >
          <div className={cn("w-full", width === "md" ? "max-w-md" : "max-w-sm")}>{children}</div>
        </main>
      </div>
    </div>
  );
}
